import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { 
  FaPlane, 
  FaHotel, 
  FaUmbrellaBeach, 
  FaMapMarkedAlt,
  FaStar,
  FaCheckCircle,
  FaPhone,
  FaEnvelope,
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaLinkedin,
  FaShieldAlt,
  FaPassport,
  FaCamera,
  FaSuitcase,
  FaClock,
  FaDollarSign,
  FaMosque,
  FaBus,
  FaUserTie,
  FaCity
} from 'react-icons/fa';
import { 
  HiOutlineSupport, 
  HiOutlineShieldCheck,
  HiOutlineClock,
  HiOutlineGlobeAlt,
  HiOutlineMail
} from 'react-icons/hi';
import type { IconType } from 'react-icons';

// Animated Card Component
const AnimatedCard: React.FC<{
  children: React.ReactNode;
  delay?: number;
  className?: string;
}> = ({ children, delay = 0, className = '' }) => {
  const { ref, isVisible } = useScrollAnimation();
  
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${
        isVisible
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-10'
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const Home: React.FC = () => {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const destinations = [
    { id: 1, name: t('destination.1.name'), desc: t('destination.1.desc'), image: 'makkah', gradient: 'from-light-green-400 via-light-green-500 to-saudi-green-500', icon: FaMosque },
    { id: 2, name: t('destination.2.name'), desc: t('destination.2.desc'), image: 'madinah', gradient: 'from-light-green-500 via-light-green-600 to-saudi-green-600', icon: FaMosque },
    { id: 3, name: t('destination.3.name'), desc: t('destination.3.desc'), image: 'riyadh', gradient: 'from-saudi-gold-300 via-saudi-gold-400 to-saudi-gold-500', icon: FaCity },
    { id: 4, name: t('destination.4.name'), desc: t('destination.4.desc'), image: 'jeddah', gradient: 'from-light-green-400 via-emerald-500 to-teal-600', icon: FaCity },
    { id: 5, name: t('destination.5.name'), desc: t('destination.5.desc'), image: 'taif', gradient: 'from-light-green-300 via-light-green-400 to-green-500', icon: FaCity },
    { id: 6, name: t('destination.6.name'), desc: t('destination.6.desc'), image: 'abha', gradient: 'from-light-green-500 via-green-500 to-emerald-600', icon: FaCity },
  ];

  const features: Array<{ icon: IconType; title: string; desc: string }> = [
    { icon: FaUserTie, title: t('feature.1.title'), desc: t('feature.1.desc') },
    { icon: HiOutlineSupport, title: t('feature.2.title'), desc: t('feature.2.desc') },
    { icon: FaBus, title: t('feature.3.title'), desc: t('feature.3.desc') },
    { icon: FaSuitcase, title: t('feature.4.title'), desc: t('feature.4.desc') },
  ];

  const services: Array<{ icon: IconType; title: string; desc: string }> = [
    { icon: FaMosque, title: t('service.1.title'), desc: t('service.1.desc') },
    { icon: FaMosque, title: t('service.2.title'), desc: t('service.2.desc') },
    { icon: FaMapMarkedAlt, title: t('service.3.title'), desc: t('service.3.desc') },
    { icon: FaBus, title: t('service.4.title'), desc: t('service.4.desc') },
    { icon: FaUserTie, title: t('service.5.title'), desc: t('service.5.desc') },
    { icon: FaCity, title: t('service.6.title'), desc: t('service.6.desc') },
  ];

  const stats = [
    { number: t('stat.1.number'), label: t('stat.1.label'), icon: FaMosque },
    { number: t('stat.2.number'), label: t('stat.2.label'), icon: FaCity },
    { number: t('stat.3.number'), label: t('stat.3.label'), icon: FaSuitcase },
    { number: t('stat.4.number'), label: t('stat.4.label'), icon: FaStar },
  ];

  const testimonials = [
    { name: t('testimonial.1.name'), text: t('testimonial.1.text'), rating: 5 },
    { name: t('testimonial.2.name'), text: t('testimonial.2.text'), rating: 5 },
    { name: t('testimonial.3.name'), text: t('testimonial.3.text'), rating: 5 },
  ];

  const tips = [
    { icon: FaMosque, title: t('tip.1.title'), desc: t('tip.1.desc') },
    { icon: FaSuitcase, title: t('tip.2.title'), desc: t('tip.2.desc') },
    { icon: FaClock, title: t('tip.3.title'), desc: t('tip.3.desc') },
    { icon: FaShieldAlt, title: t('tip.4.title'), desc: t('tip.4.desc') },
  ];

  const galleryImages = [
    { gradient: 'from-light-green-400 to-light-green-600', icon: FaCamera },
    { gradient: 'from-light-green-500 to-emerald-600', icon: FaCamera },
    { gradient: 'from-saudi-gold-300 to-saudi-gold-500', icon: FaCamera },
    { gradient: 'from-emerald-400 to-teal-600', icon: FaCamera },
    { gradient: 'from-light-green-300 to-green-500', icon: FaCamera },
    { gradient: 'from-light-green-500 to-saudi-green-600', icon: FaCamera },
  ];

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-light-green-400 via-light-green-500 to-saudi-green-500 text-white">
        {/* Geometric Pattern Background */}
        <div className="absolute inset-0 opacity-5 bg-geometric-pattern"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-light-green-400/90 via-light-green-500/90 to-saudi-green-600/90"></div>
        <div className="absolute inset-0 opacity-10">
          <svg className="absolute top-0 left-0 w-full h-full" viewBox="0 0 1200 600" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Islamic geometric pattern */}
            <circle cx="200" cy="100" r="80" fill="white" className="animate-pulse" />
            <circle cx="800" cy="200" r="60" fill="white" className="animate-pulse" style={{ animationDelay: '1s' }} />
            <circle cx="1000" cy="400" r="100" fill="white" className="animate-pulse" style={{ animationDelay: '2s' }} />
            {/* Geometric lines */}
            <path d="M0,300 Q300,200 600,300 T1200,300" stroke="white" strokeWidth="2" fill="none" opacity="0.2" />
            <path d="M0,200 Q400,100 800,200 T1600,200" stroke="white" strokeWidth="1" fill="none" opacity="0.15" />
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className={`text-center lg:text-left ${isRTL ? 'lg:text-right' : ''} animate-fadeInUp`}>
              <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                {t('hero.title')}
              </h1>
              <p className="text-xl lg:text-2xl mb-4 text-green-50 font-medium">
                {t('hero.subtitle')}
              </p>
              <p className="text-lg mb-8 text-green-100">
                {t('hero.description')}
              </p>
              <div className={`flex flex-col sm:flex-row gap-4 ${isRTL ? 'sm:flex-row-reverse' : ''} justify-center lg:justify-start`}>
                <Link
                  to="/booking"
                  className="px-8 py-4 bg-gradient-to-r from-saudi-gold-400 to-saudi-gold-500 text-white rounded-lg font-bold text-lg hover:from-saudi-gold-500 hover:to-saudi-gold-600 transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-2xl"
                >
                  {t('hero.cta')}
                </Link>
                <Link
                  to="/about"
                  className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-bold text-lg hover:bg-white hover:text-light-green-600 transform hover:scale-105 transition-all duration-300"
                >
                  {t('hero.explore')}
                </Link>
              </div>
            </div>
            <div className="hidden lg:block animate-float">
              <div className="relative w-full h-full flex items-center justify-center">
                <svg viewBox="0 0 500 500" className="w-full h-full absolute">
                  <defs>
                    <linearGradient id="planeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#ffffff', stopOpacity: 0.3 }} />
                      <stop offset="100%" style={{ stopColor: '#ffffff', stopOpacity: 0.1 }} />
                    </linearGradient>
                  </defs>
                  <circle cx="250" cy="250" r="200" fill="url(#planeGradient)" />
                </svg>
                <div className="relative z-10">
                  {/* @ts-expect-error - react-icons compatibility with React 19 */}
                  <FaMosque className="text-white" size={120} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 w-full">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-20 fill-white">
            <path d="M0,60 Q300,20 600,60 T1200,60 L1200,120 L0,120 Z"></path>
          </svg>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedCard>
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                {t('stats.title')}
              </h2>
              <p className="text-xl text-gray-600">
                {t('stats.subtitle')}
              </p>
            </div>
          </AnimatedCard>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <AnimatedCard key={index} delay={index * 100}>
                  <div className="bg-gradient-to-br from-light-green-50 to-green-50 p-8 rounded-lg text-center hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-light-green-200">
                    <div className="bg-gradient-to-br from-light-green-400 to-light-green-500 w-16 h-16 rounded-lg flex items-center justify-center mb-4 mx-auto shadow-lg">
                      {/* @ts-expect-error - react-icons compatibility with React 19 */}
                      <Icon className="text-white" size={28} />
                    </div>
                    <div className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-light-green-600 to-saudi-green-600 bg-clip-text text-transparent mb-2">
                      {stat.number}
                    </div>
                    <div className="text-gray-700 font-semibold">
                      {stat.label}
                    </div>
                  </div>
                </AnimatedCard>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedCard>
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                {t('features.title')}
              </h2>
              <p className="text-xl text-gray-600">
                {t('features.subtitle')}
              </p>
            </div>
          </AnimatedCard>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <AnimatedCard key={index} delay={index * 100}>
                  <div className="bg-white p-8 rounded-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-light-green-100 hover:border-light-green-300">
                    <div className="bg-gradient-to-br from-light-green-400 to-light-green-500 w-16 h-16 rounded-lg flex items-center justify-center mb-6 shadow-md">
                      {/* @ts-expect-error - react-icons compatibility with React 19 */}
                      <Icon className="text-white" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600">
                      {feature.desc}
                    </p>
                  </div>
                </AnimatedCard>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedCard>
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                {t('services.title')}
              </h2>
              <p className="text-xl text-gray-600">
                {t('services.subtitle')}
              </p>
            </div>
          </AnimatedCard>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <AnimatedCard key={index} delay={index * 100}>
                  <div className="group bg-gradient-to-br from-light-green-50 via-green-50 to-emerald-50 p-8 rounded-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border-2 border-light-green-200 hover:border-light-green-400">
                    <div className="bg-gradient-to-br from-light-green-400 via-light-green-500 to-saudi-green-500 w-20 h-20 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      {/* @ts-expect-error - react-icons compatibility with React 19 */}
                      <Icon className="text-white" size={32} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      {service.title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      {service.desc}
                    </p>
                    <Link
                      to="/booking"
                      className="inline-flex items-center text-light-green-600 font-semibold hover:text-light-green-700 transition-colors group-hover:translate-x-2"
                    >
                      {language === 'en' ? 'Learn More' : 'اعرف المزيد'}
                      {/* @ts-expect-error - react-icons compatibility with React 19 */}
                      <FaMapMarkedAlt className={`ml-2 ${isRTL ? 'mr-2 ml-0' : ''}`} size={16} />
                    </Link>
                  </div>
                </AnimatedCard>
              );
            })}
          </div>
        </div>
      </section>

      {/* Destinations Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedCard>
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                {t('destinations.title')}
              </h2>
              <p className="text-xl text-gray-600">
                {t('destinations.subtitle')}
              </p>
            </div>
          </AnimatedCard>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {destinations.map((destination, index) => {
              const DestinationIcon = destination.icon;
              return (
                <AnimatedCard key={destination.id} delay={index * 100}>
                  <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-4">
                    <div className={`h-64 bg-gradient-to-br ${destination.gradient} relative`}>
                      <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                      <div className="absolute bottom-4 left-4 right-4 text-white">
                        <h3 className="text-2xl font-bold mb-1">{destination.name}</h3>
                        <p className="text-white opacity-90">{destination.desc}</p>
                      </div>
                      {/* @ts-expect-error - react-icons compatibility with React 19 */}
                      <DestinationIcon className="absolute top-4 right-4 text-white opacity-50 group-hover:opacity-100 transition-opacity" size={32} />
                    </div>
                    <div className="p-6 bg-white">
                      <Link
                        to="/booking"
                        className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-700 transition-colors"
                      >
                      {language === 'en' ? 'Explore' : 'استكشف'}
                      {/* @ts-expect-error - react-icons compatibility with React 19 */}
                      <FaMapMarkedAlt className={`ml-2 ${isRTL ? 'mr-2 ml-0' : ''} text-light-green-600`} size={16} />
                      </Link>
                    </div>
                  </div>
                </AnimatedCard>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedCard>
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                {t('gallery.title')}
              </h2>
              <p className="text-xl text-gray-600">
                {t('gallery.subtitle')}
              </p>
            </div>
          </AnimatedCard>
          <div className="grid md:grid-cols-3 gap-6">
            {galleryImages.map((item, index) => {
              const Icon = item.icon;
              return (
                <AnimatedCard key={index} delay={index * 100}>
                  <div className={`group relative h-64 bg-gradient-to-br ${item.gradient} rounded-2xl overflow-hidden cursor-pointer transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-2xl`}>
                    <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-30 transition-opacity duration-300"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      {/* @ts-expect-error - react-icons compatibility with React 19 */}
                      <Icon className="text-white opacity-50 group-hover:opacity-100 transition-opacity" size={48} />
                    </div>
                  </div>
                </AnimatedCard>
              );
            })}
          </div>
        </div>
      </section>

      {/* Travel Tips Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedCard>
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                {t('tips.title')}
              </h2>
              <p className="text-xl text-gray-600">
                {t('tips.subtitle')}
              </p>
            </div>
          </AnimatedCard>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {tips.map((tip, index) => {
              const Icon = tip.icon;
              return (
                <AnimatedCard key={index} delay={index * 100}>
                  <div className="bg-white p-6 rounded-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-light-green-500">
                    <div className="bg-gradient-to-br from-light-green-100 to-green-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                      {/* @ts-expect-error - react-icons compatibility with React 19 */}
                      <Icon className="text-light-green-600" size={24} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">
                      {tip.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {tip.desc}
                    </p>
                  </div>
                </AnimatedCard>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedCard>
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                {t('testimonials.title')}
              </h2>
              <p className="text-xl text-gray-600">
                {t('testimonials.subtitle')}
              </p>
            </div>
          </AnimatedCard>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
                <AnimatedCard key={index} delay={index * 100}>
                  <div className="bg-gradient-to-br from-light-green-50 to-green-50 p-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-light-green-200">
                    <div className="flex mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        // @ts-expect-error - react-icons compatibility with React 19
                        <FaStar key={i} className="text-saudi-gold-500" size={20} />
                      ))}
                    </div>
                    <p className="text-gray-700 mb-6 text-lg leading-relaxed">
                      "{testimonial.text}"
                    </p>
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-gradient-to-br from-light-green-400 to-light-green-500 rounded-lg flex items-center justify-center text-white font-bold mr-4 shadow-md">
                        {testimonial.name.charAt(0)}
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                        <p className="text-gray-600 text-sm">{language === 'en' ? 'Verified Traveler' : 'مسافر موثق'}</p>
                      </div>
                    </div>
                  </div>
                </AnimatedCard>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-gradient-to-r from-light-green-400 via-light-green-500 to-saudi-green-500 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5 bg-geometric-pattern"></div>
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 1200 400" fill="none">
            <circle cx="100" cy="100" r="50" fill="white" className="animate-pulse" />
            <circle cx="1100" cy="300" r="80" fill="white" className="animate-pulse" style={{ animationDelay: '1s' }} />
          </svg>
        </div>
        <AnimatedCard>
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            {/* @ts-expect-error - react-icons compatibility with React 19 */}
            <HiOutlineMail className="mx-auto mb-6" size={64} />
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">
              {t('newsletter.title')}
            </h2>
            <p className="text-xl mb-8 text-blue-100">
              {t('newsletter.subtitle')}
            </p>
            {subscribed ? (
              <div className="bg-white text-light-green-600 px-8 py-4 rounded-lg font-semibold inline-block">
                {t('newsletter.success')}
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('newsletter.placeholder')}
                  className="flex-1 px-6 py-4 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-white"
                  required
                />
                <button
                  type="submit"
                  className="px-8 py-4 bg-gradient-to-r from-saudi-gold-400 to-saudi-gold-500 text-white rounded-lg font-bold hover:from-saudi-gold-500 hover:to-saudi-gold-600 transform hover:scale-105 transition-all duration-300 shadow-xl"
                >
                  {t('newsletter.button')}
                </button>
              </form>
            )}
          </div>
        </AnimatedCard>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <AnimatedCard>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              {t('cta.title')}
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              {t('cta.subtitle')}
            </p>
            <Link
              to="/booking"
              className="inline-block px-10 py-5 bg-gradient-to-r from-light-green-500 via-light-green-600 to-saudi-green-600 text-white rounded-lg font-bold text-xl hover:from-light-green-600 hover:via-light-green-700 hover:to-saudi-green-700 transform hover:scale-105 transition-all duration-300 shadow-2xl"
            >
              {t('cta.button')}
            </Link>
          </div>
        </AnimatedCard>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img src="/assets/logo.svg" alt="TourBook Logo" className="h-10 mb-4 brightness-0 invert" />
              <p className="text-gray-400">
                {t('footer.description')}
              </p>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">{t('footer.quickLinks')}</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/" className="hover:text-white transition-colors">{t('nav.home')}</Link></li>
                <li><Link to="/booking" className="hover:text-white transition-colors">{t('nav.booking')}</Link></li>
                <li><Link to="/about" className="hover:text-white transition-colors">{t('nav.about')}</Link></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">{t('nav.blog')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">{t('footer.contact')}</h4>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center">
                  {/* @ts-expect-error - react-icons compatibility with React 19 */}
                  <FaPhone className="mr-2" size={16} />
                  <span>+1 (555) 123-4567</span>
                </li>
                <li className="flex items-center">
                  {/* @ts-expect-error - react-icons compatibility with React 19 */}
                  <FaEnvelope className="mr-2" size={16} />
                  <span>info@tourbook.com</span>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">{t('footer.follow')}</h4>
              <div className="flex space-x-4 rtl:space-x-reverse">
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  {/* @ts-expect-error - react-icons compatibility with React 19 */}
                  <FaFacebook size={24} />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  {/* @ts-expect-error - react-icons compatibility with React 19 */}
                  <FaTwitter size={24} />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  {/* @ts-expect-error - react-icons compatibility with React 19 */}
                  <FaInstagram size={24} />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  {/* @ts-expect-error - react-icons compatibility with React 19 */}
                  <FaLinkedin size={24} />
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} TourBook. {t('footer.rights')}.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
